import * as React from 'react';

import { Col, Form, Input, Row } from 'antd';

import { PrimitiveTreeSelect } from 'components';
import { useRoleValue } from 'context';
import type { RoleScope, RoleScopeAction, RoleScopeType } from 'types';
import {
  generateTreeSelectData,
  getObjectEntries,
  inputRule,
  localizeRoleScopeAction,
  localizeRoleScopeType,
} from 'utils';

const roleScopes: Record<RoleScopeType, RoleScope[]> = {
  product: [
    'product:read',
    'product:create',
    'product:modify',
    'product:delete',
    'product:publish',
  ],
  category: [
    'category:read',
    'category:create',
    'category:modify',
    'category:delete',
  ],
  tag: ['tag:read', 'tag:create', 'tag:modify', 'tag:delete'],
  brand: ['brand:read', 'brand:create', 'brand:modify', 'brand:delete'],
  catalog: [
    'catalog:read',
    'catalog:create',
    'catalog:modify',
    'catalog:delete',
  ],
  'attribute-config': [
    'attribute-config:read',
    'attribute-config:create',
    'attribute-config:modify',
    'attribute-config:delete',
  ],
  'packaging-model': [
    'packaging-model:read',
    'packaging-model:create',
    'packaging-model:modify',
    'packaging-model:delete',
  ],
  purchase: [
    'purchase:read',
    'purchase:create',
    'purchase:modify',
    'purchase:delete',
  ],
  'payment-gateway': [
    'payment-gateway:read',
    'payment-gateway:create',
    'payment-gateway:modify',
    'payment-gateway:delete',
  ],
  user: ['user:read', 'user:create', 'user:modify', 'user:delete'],
  'product-comment': [
    'product-comment:read',
    'product-comment:create',
    'product-comment:modify',
    'product-comment:delete',
  ],
  discount: [
    'discount:read',
    'discount:create',
    'discount:modify',
    'discount:delete',
  ],
  'special-offer': [
    'special-offer:read',
    'special-offer:create',
    'special-offer:modify',
    'special-offer:delete',
  ],
  config: ['config:read', 'config:create', 'config:modify', 'config:delete'],
  'shipping-config': [
    'shipping-config:read',
    'shipping-config:create',
    'shipping-config:modify',
    'shipping-config:delete',
  ],
};

type FieldType = Pick<ReturnType<typeof useRoleValue>, 'name' | 'scopes'>;

const RoleFormContent = () => {
  const { name, scopes = [] } = useRoleValue();

  const roleScopesTreeData = generateTreeSelectData<
    Record<'id', string> & Record<'title' | 'value', unknown>,
    'id',
    'title',
    'value'
  >(
    getObjectEntries(roleScopes).map(([roleScopeKey, roleScopeValues]) => {
      const { label: roleScopeKeyLabel } = localizeRoleScopeType(roleScopeKey);

      return {
        id: roleScopeKey,
        title: roleScopeKeyLabel,
        value: roleScopeKey,
        children: roleScopeValues.map(roleScopeValue => {
          const [roleScopeValueType, roleScopeValueAction] =
            roleScopeValue.split(':') as [RoleScopeType, RoleScopeAction];
          const { label: roleScopeValueTypeLabel } =
            localizeRoleScopeType(roleScopeValueType);
          const { label: roleScopeValueActionLabel } =
            localizeRoleScopeAction(roleScopeValueAction);

          return {
            id: roleScopeValue,
            title: `${roleScopeValueTypeLabel}: ${roleScopeValueActionLabel}`,
            value: roleScopeValue,
          };
        }),
      };
    }),
    'id',
    'title',
    'value',
  );

  return (
    <Row className='px-6 py-8'>
      <Col span={24}>
        <Form.Item<FieldType>
          label='نام'
          name='name'
          rules={[inputRule.required, inputRule.string]}
          initialValue={name}
        >
          <Input placeholder='نام دسترسی را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType>
          label='سطح‌ها'
          name='scopes'
          initialValue={scopes}
        >
          <PrimitiveTreeSelect
            treeCheckable
            treeData={roleScopesTreeData}
            placeholder='سطح‌های دسترسی را انتخاب کنید'
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { RoleFormContent };
